import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ClientDashboardTabs from '../../components/navigation/ClientDashboardTabs';

import Icon from '../../components/AppIcon';
import EmbedCodeGenerator from './components/EmbedCodeGenerator';
import ReferenceCodeLookup from './components/ReferenceCodeLookup';
import LinkAnalytics from './components/LinkAnalytics';
import { companyService } from '../../services/companyService';

const EmbedLinksManagement = () => {
  const navigate = useNavigate();
  const { companyIdentifier } = useParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('embed');

  useEffect(() => {
    // If accessed via company-specific route, load company data
    if (companyIdentifier) {
      const loadCompanyData = async () => {
        try {
          setLoading(true);
          // Extract slug from compound identifier
          const extractSlugFromIdentifier = (identifier) => {
            const parts = identifier?.split('-');
            let uuidStartIndex = -1;
            for (let i = parts?.length - 1; i >= 0; i--) {
              if (parts?.[i]?.length === 12 && /^[a-f0-9]+$/i?.test(parts?.[i])) {
                if (i >= 4 && 
                    parts?.[i-1]?.length === 4 && /^[a-f0-9]+$/i?.test(parts?.[i-1]) &&
                    parts?.[i-2]?.length === 4 && /^[a-f0-9]+$/i?.test(parts?.[i-2]) &&
                    parts?.[i-3]?.length === 4 && /^[a-f0-9]+$/i?.test(parts?.[i-3]) &&
                    parts?.[i-4]?.length === 8 && /^[a-f0-9]+$/i?.test(parts?.[i-4])) {
                  uuidStartIndex = i - 4;
                  break;
                }
              }
            }
            if (uuidStartIndex > 0) {
              return parts?.slice(0, uuidStartIndex)?.join('-');
            }
            return identifier;
          };
          
          const slug = extractSlugFromIdentifier(companyIdentifier);
          const normalizedSlug = decodeURIComponent(slug || '')?.toLowerCase()?.trim();
          
          console.log('Loading company data for slug:', normalizedSlug);
          
          const companyData = await companyService?.getBySlug(normalizedSlug);
          
          if (companyData) {
            setCompany(companyData);
            console.log('Company data loaded:', companyData);
          } else {
            console.warn('Company not found for slug:', normalizedSlug);
          }
        } catch (error) {
          console.error('Error loading company data:', error);
        } finally {
          setLoading(false);
        }
      };
      
      loadCompanyData();
    } else {
      setLoading(false);
    }
  }, [companyIdentifier]);

  const tabs = [
    { id: 'embed', label: 'Embed Code', icon: 'Code' },
    { id: 'lookup', label: 'Reference Lookup', icon: 'Search' },
    { id: 'analytics', label: 'Analytics', icon: 'BarChart3' }
  ];

  // Generate company-specific base URL
  const getBaseUrl = () => {
    if (companyIdentifier) {
      return `https://epoxyviz.pro/client/${companyIdentifier}/embed`;
    }
    return `https://epoxyviz.pro/embed`;
  };

  return (
    <div className="min-h-screen bg-background">
      {companyIdentifier && <ClientDashboardTabs />}
      <div className={companyIdentifier ? "main-content-with-tabs" : ""}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-text-primary mb-2">
              Embed Links & Integration
            </h1>
            <p className="text-text-secondary">
              Deploy your visualizer across multiple platforms and manage customer reference codes
            </p>
          </div>

          <div className="bg-card border border-border rounded-lg mb-6 overflow-hidden">
            <div className="flex overflow-x-auto scrollbar-hide border-b border-border">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`
                    flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap
                    border-b-2 transition-all duration-150
                    ${activeTab === tab?.id
                      ? 'border-primary text-primary bg-primary/5' :'border-transparent text-text-secondary hover:text-text-primary hover:bg-muted/50'
                    }
                  `}
                >
                  <Icon name={tab?.icon} size={18} />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>

          {activeTab === 'embed' && (
            <div className="space-y-6">
              <EmbedCodeGenerator 
                baseUrl={getBaseUrl()} 
                companyIdentifier={companyIdentifier}
                companyName={company?.name}
              />

              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-text-primary mb-4">
                  Live Preview
                </h3>
                <div className="bg-muted rounded-lg p-4 border border-border">
                  <div className="aspect-video bg-background rounded border border-border flex items-center justify-center">
                    <div className="text-center">
                      <Icon name="Monitor" size={48} className="mx-auto mb-4 text-text-secondary opacity-50" />
                      <p className="text-sm text-text-secondary">
                        Visualizer preview will appear here
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Icon name="Smartphone" size={20} color="var(--color-primary)" />
                    <h3 className="text-lg font-semibold text-text-primary">Mobile Compatibility</h3>
                  </div>
                  <p className="text-sm text-text-secondary mb-4">
                    The embed code is fully responsive and optimized for mobile devices with touch-friendly controls.
                  </p>
                  <div className="flex items-center gap-2 text-xs text-success">
                    <Icon name="Check" size={14} />
                    <span>Tested on iOS and Android</span>
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Icon name="Zap" size={20} color="var(--color-primary)" />
                    <h3 className="text-lg font-semibold text-text-primary">Performance</h3>
                  </div>
                  <p className="text-sm text-text-secondary mb-4">
                    Lazy loading and optimized assets ensure fast page load times without impacting your website performance.
                  </p>
                  <div className="flex items-center gap-2 text-xs text-success">
                    <Icon name="Check" size={14} />
                    <span>Lighthouse score: 95+</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'lookup' && (
            <div className="space-y-6">
              <ReferenceCodeLookup />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <Icon name="Shield" size={24} color="var(--color-primary)" />
                  </div>
                  <h3 className="text-lg font-semibold text-text-primary mb-2">
                    Secure Access
                  </h3>
                  <p className="text-sm text-text-secondary">
                    Reference codes are unique and can only be accessed by your company
                  </p>
                </div>

                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <Icon name="Clock" size={24} color="var(--color-primary)" />
                  </div>
                  <h3 className="text-lg font-semibold text-text-primary mb-2">
                    Instant Retrieval
                  </h3>
                  <p className="text-sm text-text-secondary">
                    Look up any customer project instantly with their reference code
                  </p>
                </div>

                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <Icon name="FileText" size={24} color="var(--color-primary)" />
                  </div>
                  <h3 className="text-lg font-semibold text-text-primary mb-2">
                    Complete Details
                  </h3>
                  <p className="text-sm text-text-secondary">
                    View all project specifications and customer information
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="space-y-6">
              <LinkAnalytics />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Icon name="Target" size={20} color="var(--color-primary)" />
                    <h3 className="text-lg font-semibold text-text-primary">Conversion Tracking</h3>
                  </div>
                  <p className="text-sm text-text-secondary mb-4">
                    Monitor how many visitors complete the visualizer flow and generate reference codes.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-text-secondary">Total Visitors</span>
                      <span className="font-semibold text-text-primary">4,660</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-text-secondary">Completed Flows</span>
                      <span className="font-semibold text-text-primary">546</span>
                    </div>
                    <div className="flex items-center justify-between text-sm pt-2 border-t border-border">
                      <span className="text-text-secondary">Overall Rate</span>
                      <span className="font-semibold text-primary">11.7%</span>
                    </div>
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Icon name="Users" size={20} color="var(--color-primary)" />
                    <h3 className="text-lg font-semibold text-text-primary">User Engagement</h3>
                  </div>
                  <p className="text-sm text-text-secondary mb-4">
                    Track average time spent and interaction patterns across different deployment methods.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-text-secondary">Avg. Session Time</span>
                      <span className="font-semibold text-text-primary">4m 32s</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-text-secondary">Bounce Rate</span>
                      <span className="font-semibold text-text-primary">23.4%</span>
                    </div>
                    <div className="flex items-center justify-between text-sm pt-2 border-t border-border">
                      <span className="text-text-secondary">Return Visitors</span>
                      <span className="font-semibold text-primary">18.9%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmbedLinksManagement;